import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCookies } from "@/context/CookieContext";

export default function CookieBanner() {
  const { t, to } = useLang();
  const { decided, acceptAll, rejectAll, savePreferences, consent } = useCookies();
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: consent.analytics, marketing: consent.marketing });

  if (decided) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-5" role="dialog" aria-modal="false" aria-label={t.cookies.title} data-testid="cookie-banner">
      <div className="pointer-events-auto mx-auto max-w-3xl overflow-hidden rounded-2xl border border-nek-border bg-white shadow-lift">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-nek-blue/10 text-nek-blue">
              <Cookie className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-heading text-base font-bold text-nek-navy">{t.cookies.title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-nek-gray">
                {t.cookies.text}{" "}
                <Link to={to("/politique-cookies")} className="font-semibold text-nek-blue underline">{t.cookies.policyLink}</Link>
              </p>
            </div>
          </div>

          {customize && (
            <div className="mt-4 space-y-2.5" data-testid="cookie-customize">
              <PrefRow title={t.cookies.necessary} desc={t.cookies.necessaryDesc} checked disabled note={t.cookies.always} />
              <PrefRow title={t.cookies.analytics} desc={t.cookies.analyticsDesc} checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testid="cookie-analytics" />
              <PrefRow title={t.cookies.marketing} desc={t.cookies.marketingDesc} checked={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} testid="cookie-marketing" />
            </div>
          )}

          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {customize ? (
              <button type="button" onClick={() => savePreferences(prefs)} data-testid="cookie-save" className="btn-navy flex-1 !py-3">{t.cookies.save}</button>
            ) : (
              <button type="button" onClick={acceptAll} data-testid="cookie-accept-all" className="btn-coral flex-1 !py-3">{t.cookies.acceptAll}</button>
            )}
            <button type="button" onClick={rejectAll} data-testid="cookie-reject-all" className="btn-outline flex-1 !py-3">{t.cookies.rejectAll}</button>
            {!customize && (
              <button type="button" onClick={() => setCustomize(true)} data-testid="cookie-customize-btn" className="btn-outline flex-1 !py-3">{t.cookies.customize}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, disabled, onChange, note, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-nek-light px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-nek-navy">{title}</p>
        <p className="text-xs text-nek-gray">{desc}</p>
      </div>
      {disabled ? (
        <span className="shrink-0 text-xs font-semibold text-nek-green">{note}</span>
      ) : (
        <label className="relative inline-flex cursor-pointer items-center">
          <input type="checkbox" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
          <span className="h-6 w-11 rounded-full bg-nek-border transition-colors peer-checked:bg-nek-blue after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-5" />
        </label>
      )}
    </div>
  );
}
